import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Paper,
} from '@mui/material';
import { Icon } from '../../icons';
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from '../../constants/color';
import { useAuth } from '../../context/authContext';

const TechnicalObservations = () => {
  const { userData } = useAuth();
  const [observations, setObservations] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newObservation, setNewObservation] = useState({
    title: '',
    project: '',
    description: '',
    priority: 'medium',
  });

  useEffect(() => {
    // Mock data - replace with actual API calls
    setObservations([
      {
        id: 1,
        title: 'Foundation Inspection',
        project: 'Building A',
        description: 'Detailed inspection of foundation work and structural integrity',
        status: 'pending',
        date: '2026-02-03',
        priority: 'high',
        createdBy: 'John Doe',
      },
      {
        id: 2,
        title: 'Structural Analysis',
        project: 'Bridge B',
        description: 'Analysis of structural components and load testing',
        status: 'approved',
        date: '2026-02-02',
        priority: 'medium',
        createdBy: 'Jane Smith',
      },
      {
        id: 3,
        title: 'Quality Control Check',
        project: 'Tower C',
        description: 'Quality control assessment of construction materials',
        status: 'rejected',
        date: '2026-02-01',
        priority: 'low',
        createdBy: 'Mike Johnson',
      },
      {
        id: 4,
        title: 'Safety Inspection',
        project: 'Complex D',
        description: 'Safety protocols and equipment verification',
        status: 'pending',
        date: '2026-01-31',
        priority: 'high',
        createdBy: 'Sarah Wilson',
      },
    ]);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleCreateObservation = () => {
    // Handle creating new observation
    console.log('Creating observation:', newObservation);
    setCreateDialogOpen(false);
    setNewObservation({
      title: '',
      project: '',
      description: '',
      priority: 'medium',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H1,
            color: COLORS.TEXT,
          }}
        >
          Technical Observations
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<Icon name="plus" size={16} />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            backgroundColor: COLORS.PRIMARY,
            '&:hover': {
              backgroundColor: COLORS.SECONDARY,
            },
            fontFamily: FONT_FAMILY,
            textTransform: 'none',
          }}
        >
          Create Observation
        </Button>
      </Box>

      {/* Observations Table */}
      <Paper
        sx={{
          backgroundColor: COLORS.BACKGROUND,
          border: `1px solid ${COLORS.BORDER}`,
          borderRadius: 2,
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Title
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Project
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Priority
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Created By
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    color: COLORS.TEXT,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {observations.map((observation) => (
                <TableRow key={observation.id}>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      fontWeight: 500,
                    }}
                  >
                    {observation.title}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    {observation.project}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {observation.description}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={observation.status}
                      color={getStatusColor(observation.status)}
                      size="small"
                      sx={{
                        fontFamily: FONT_FAMILY,
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={observation.priority}
                      color={getPriorityColor(observation.priority)}
                      size="small"
                      sx={{
                        fontFamily: FONT_FAMILY,
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    {observation.createdBy}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    {observation.date}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small">
                      <Icon name="eye" size={16} color={COLORS.PRIMARY} />
                    </IconButton>
                    <IconButton size="small">
                      <Icon name="edit" size={16} color={COLORS.PRIMARY} />
                    </IconButton>
                    <IconButton size="small">
                      <Icon name="trash" size={16} color={COLORS.ERROR} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Observation Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H2,
            color: COLORS.TEXT,
          }}
        >
          Create New Observation
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title"
                value={newObservation.title}
                onChange={(e) => setNewObservation({ ...newObservation, title: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: FONT_FAMILY,
                  },
                  '& .MuiInputBase-root': {
                    fontFamily: FONT_FAMILY,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project"
                value={newObservation.project}
                onChange={(e) => setNewObservation({ ...newObservation, project: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: FONT_FAMILY,
                  },
                  '& .MuiInputBase-root': {
                    fontFamily: FONT_FAMILY,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={newObservation.description}
                onChange={(e) => setNewObservation({ ...newObservation, description: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: FONT_FAMILY,
                  },
                  '& .MuiInputBase-root': {
                    fontFamily: FONT_FAMILY,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Priority"
                value={newObservation.priority}
                onChange={(e) => setNewObservation({ ...newObservation, priority: e.target.value })}
                SelectProps={{
                  native: true,
                }}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: FONT_FAMILY,
                  },
                  '& .MuiInputBase-root': {
                    fontFamily: FONT_FAMILY,
                  },
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            variant="outlined"
            sx={{
              fontFamily: FONT_FAMILY,
              textTransform: 'none',
              borderColor: COLORS.PRIMARY,
              color: COLORS.PRIMARY,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateObservation}
            variant="contained"
            sx={{
              fontFamily: FONT_FAMILY,
              textTransform: 'none',
              backgroundColor: COLORS.PRIMARY,
              '&:hover': {
                backgroundColor: COLORS.SECONDARY,
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicalObservations;
